import { View, Text, StyleSheet, Platform } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <View style={styles.container}>
      <WebScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={styles.title}>Terms of Service</Text>
            <Text style={styles.lastUpdated}>Last Updated: December 11, 2024</Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Acceptance of Terms</Text>
            <Text style={styles.paragraph}>
              Welcome to HoneyDone ("we," "our," or "us"). By accessing or using our home maintenance management 
              platform, website, and mobile application (collectively, the "Service"), you agree to be bound by 
              these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
            </Text>
            <Text style={styles.paragraph}>
              We reserve the right to modify these Terms at any time. We will notify you of material changes by 
              posting the updated Terms on our Service and updating the "Last Updated" date. Your continued use 
              of the Service after changes are posted constitutes your acceptance of the modified Terms.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Description of Service</Text>
            <Text style={styles.paragraph}>
              HoneyDone provides a comprehensive home maintenance management platform that helps homeowners:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Track and manage home maintenance tasks</Text>
              <Text style={styles.listItem}>• Create and monitor service requests</Text>
              <Text style={styles.listItem}>• Coordinate with service providers</Text>
              <Text style={styles.listItem}>• Maintain digital records of home-related expenses and documents</Text>
              <Text style={styles.listItem}>• Schedule and track home visits</Text>
              <Text style={styles.listItem}>• Manage home inventory and important information</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>3. User Accounts</Text>
            
            <Text style={styles.subsectionTitle}>3.1 Registration</Text>
            <Text style={styles.paragraph}>
              To use certain features of our Service, you must register for an account. You agree to:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Provide accurate, current, and complete information</Text>
              <Text style={styles.listItem}>• Maintain and update your information to keep it accurate and current</Text>
              <Text style={styles.listItem}>• Maintain the security of your password and account</Text>
              <Text style={styles.listItem}>• Accept responsibility for all activities that occur under your account</Text>
              <Text style={styles.listItem}>• Notify us immediately of any unauthorized use of your account</Text>
            </View>

            <Text style={styles.subsectionTitle}>3.2 Eligibility</Text>
            <Text style={styles.paragraph}>
              You must be at least 18 years old to use our Service. By using the Service, you represent and 
              warrant that you meet this age requirement.
            </Text>

            <Text style={styles.subsectionTitle}>3.3 Account Termination</Text>
            <Text style={styles.paragraph}>
              We reserve the right to suspend or terminate your account at any time for any reason, including 
              violation of these Terms. You may delete your account at any time through your account settings.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>4. User Conduct</Text>
            <Text style={styles.paragraph}>You agree not to:</Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Use the Service for any illegal purpose or in violation of any laws</Text>
              <Text style={styles.listItem}>• Violate the intellectual property rights of others</Text>
              <Text style={styles.listItem}>• Upload or transmit viruses, malware, or other harmful code</Text>
              <Text style={styles.listItem}>• Attempt to gain unauthorized access to any part of the Service</Text>
              <Text style={styles.listItem}>• Interfere with or disrupt the Service or servers</Text>
              <Text style={styles.listItem}>• Impersonate any person or entity</Text>
              <Text style={styles.listItem}>• Harass, abuse, or harm another person</Text>
              <Text style={styles.listItem}>• Use automated systems to access the Service without permission</Text>
              <Text style={styles.listItem}>• Collect or store personal data about other users without consent</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>5. Content</Text>
            
            <Text style={styles.subsectionTitle}>5.1 Your Content</Text>
            <Text style={styles.paragraph}>
              You retain ownership of all content you upload, post, or transmit through the Service ("Your Content"). 
              By submitting Your Content, you grant us a worldwide, non-exclusive, royalty-free license to use, 
              store, display, and reproduce Your Content solely for the purpose of providing and improving the Service.
            </Text>

            <Text style={styles.subsectionTitle}>5.2 Content Responsibility</Text>
            <Text style={styles.paragraph}>
              You are solely responsible for Your Content. You represent and warrant that:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• You own or have the necessary rights to Your Content</Text>
              <Text style={styles.listItem}>• Your Content does not violate any third-party rights</Text>
              <Text style={styles.listItem}>• Your Content does not contain illegal or harmful material</Text>
            </View>

            <Text style={styles.subsectionTitle}>5.3 Our Content</Text>
            <Text style={styles.paragraph}>
              All content provided by HoneyDone, including text, graphics, logos, and software, is owned by us or 
              our licensors and is protected by intellectual property laws. You may not use our content without 
              our written permission.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>6. Service Providers</Text>
            <Text style={styles.paragraph}>
              HoneyDone may facilitate connections between you and third-party service providers (e.g., contractors, 
              handymen, cleaning services). We are not responsible for:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• The quality, safety, or legality of services provided by third parties</Text>
              <Text style={styles.listItem}>• The actions or omissions of service providers</Text>
              <Text style={styles.listItem}>• Disputes between you and service providers</Text>
              <Text style={styles.listItem}>• Any damages or losses resulting from third-party services</Text>
            </View>
            <Text style={styles.paragraph}>
              You are solely responsible for evaluating, selecting, and contracting with service providers. We 
              recommend verifying credentials, licenses, and insurance before hiring any service provider.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>7. Payments and Subscriptions</Text>
            
            <Text style={styles.subsectionTitle}>7.1 Fees</Text>
            <Text style={styles.paragraph}>
              Certain features of our Service may require payment of fees. All fees are stated in U.S. dollars and 
              are non-refundable unless otherwise stated or required by law.
            </Text>

            <Text style={styles.subsectionTitle}>7.2 Payment Processing</Text>
            <Text style={styles.paragraph}>
              Payments are processed by our third-party payment processor, Stripe. By providing payment information, 
              you authorize us to charge the applicable fees to your payment method.
            </Text>

            <Text style={styles.subsectionTitle}>7.3 Subscriptions</Text>
            <Text style={styles.paragraph}>
              If you subscribe to a paid plan, your subscription will automatically renew at the end of each 
              billing period unless you cancel. You may cancel your subscription at any time through your account 
              settings.
            </Text>

            <Text style={styles.subsectionTitle}>7.4 Price Changes</Text>
            <Text style={styles.paragraph}>
              We reserve the right to change our fees at any time. We will provide at least 30 days' notice of 
              any price increases. Your continued use of the Service after price changes constitutes acceptance 
              of the new fees.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>8. SMS Communications</Text>
            <Text style={styles.paragraph}>
              By providing your phone number, you consent to receive SMS text messages from HoneyDone for:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Account verification and security alerts</Text>
              <Text style={styles.listItem}>• Service appointment reminders</Text>
              <Text style={styles.listItem}>• Important updates about your service requests</Text>
              <Text style={styles.listItem}>• Maintenance task reminders</Text>
            </View>
            <Text style={styles.paragraph}>
              You may opt out of SMS messages at any time by replying "STOP" or updating your notification 
              preferences. Message and data rates may apply.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>9. Disclaimers</Text>
            
            <Text style={styles.subsectionTitle}>9.1 "As Is" Service</Text>
            <Text style={styles.paragraph}>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
              PURPOSE, OR NON-INFRINGEMENT.
            </Text>

            <Text style={styles.subsectionTitle}>9.2 No Guarantee</Text>
            <Text style={styles.paragraph}>
              We do not guarantee that:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• The Service will be uninterrupted, secure, or error-free</Text>
              <Text style={styles.listItem}>• Defects will be corrected</Text>
              <Text style={styles.listItem}>• The Service is free of viruses or harmful components</Text>
              <Text style={styles.listItem}>• Results obtained from the Service will be accurate or reliable</Text>
            </View>

            <Text style={styles.subsectionTitle}>9.3 Professional Advice</Text>
            <Text style={styles.paragraph}>
              The Service provides information and tools for home maintenance management but does not constitute 
              professional advice. You should consult qualified professionals for specific home maintenance, 
              construction, or legal matters.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>10. Limitation of Liability</Text>
            <Text style={styles.paragraph}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, HONEYDONE AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, 
              AND AGENTS SHALL NOT BE LIABLE FOR:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Indirect, incidental, special, consequential, or punitive damages</Text>
              <Text style={styles.listItem}>• Loss of profits, revenue, data, or use</Text>
              <Text style={styles.listItem}>• Property damage or personal injury</Text>
              <Text style={styles.listItem}>• Damages arising from service provider actions or omissions</Text>
            </View>
            <Text style={styles.paragraph}>
              Our total liability to you for any claims arising from or related to these Terms or the Service 
              shall not exceed the amount you paid us in the 12 months preceding the claim, or $100, whichever 
              is greater.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>11. Indemnification</Text>
            <Text style={styles.paragraph}>
              You agree to indemnify, defend, and hold harmless HoneyDone and its affiliates, officers, directors, 
              employees, and agents from any claims, liabilities, damages, losses, and expenses arising from:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Your use of the Service</Text>
              <Text style={styles.listItem}>• Your violation of these Terms</Text>
              <Text style={styles.listItem}>• Your violation of any third-party rights</Text>
              <Text style={styles.listItem}>• Your Content</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>12. Dispute Resolution</Text>
            
            <Text style={styles.subsectionTitle}>12.1 Governing Law</Text>
            <Text style={styles.paragraph}>
              These Terms are governed by the laws of the United States and the State of [Your State], without 
              regard to conflict of law principles.
            </Text>

            <Text style={styles.subsectionTitle}>12.2 Arbitration</Text>
            <Text style={styles.paragraph}>
              Any disputes arising from these Terms or the Service shall be resolved through binding arbitration 
              in accordance with the American Arbitration Association's rules, except where prohibited by law.
            </Text>

            <Text style={styles.subsectionTitle}>12.3 Class Action Waiver</Text>
            <Text style={styles.paragraph}>
              You agree to resolve disputes on an individual basis only and waive any right to participate in a 
              class action lawsuit or class-wide arbitration.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>13. General Provisions</Text>
            
            <Text style={styles.subsectionTitle}>13.1 Entire Agreement</Text>
            <Text style={styles.paragraph}>
              These Terms constitute the entire agreement between you and HoneyDone regarding the Service.
            </Text>

            <Text style={styles.subsectionTitle}>13.2 Severability</Text>
            <Text style={styles.paragraph}>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions 
              will remain in full force and effect.
            </Text>

            <Text style={styles.subsectionTitle}>13.3 Waiver</Text>
            <Text style={styles.paragraph}>
              Our failure to enforce any right or provision of these Terms does not constitute a waiver of that 
              right or provision.
            </Text>

            <Text style={styles.subsectionTitle}>13.4 Assignment</Text>
            <Text style={styles.paragraph}>
              You may not assign or transfer these Terms or your account without our written consent. We may 
              assign these Terms without restriction.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>14. Contact Information</Text>
            <Text style={styles.paragraph}>
              If you have questions about these Terms, please contact us:
            </Text>
            <View style={styles.contactInfo}>
              <Text style={styles.paragraph}>HoneyDone by Dunzo Group Incorporated</Text>
              <Text style={styles.paragraph}>Email: hello@honeydone.com</Text>
            </View>
          </View>

          <View style={styles.footer}>
            <Link to="/dashboard" style={styles.link}>
              <Text style={styles.linkText}>← Back to Dashboard</Text>
            </Link>
          </View>
        </View>
      </WebScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    maxWidth: 896,
    marginHorizontal: 'auto' as any,
    paddingHorizontal: 16,
    paddingVertical: 48,
    ...(Platform.OS === 'web' && {
      paddingHorizontal: 24,
    }),
  },
  header: {
    marginBottom: 48,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
  },
  lastUpdated: {
    fontSize: 14,
    color: '#6b7280',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 16,
  },
  subsectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
    color: '#4b5563',
    marginBottom: 12,
  },
  list: {
    marginLeft: 16,
    marginBottom: 12,
  },
  listItem: {
    fontSize: 16,
    lineHeight: 24,
    color: '#4b5563',
    marginBottom: 8,
  },
  contactInfo: {
    backgroundColor: '#f3f4f6',
    padding: 20,
    borderRadius: 8,
    marginTop: 16,
  },
  footer: {
    marginTop: 48,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  link: {
    textDecorationLine: 'none',
  },
  linkText: {
    fontSize: 16,
    color: '#000000',
    fontWeight: '500',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
});

export default TermsOfService;
